import React from "react";

const PageSizeSelector = ({ pageSize, onPageSizeChange }) => {
  const handleSizeChange = (newSize) => {
    // When changing page size, we want to apply the change and reset to page 1
    onPageSizeChange(newSize);
  };

  return (
    <div className="flex items-center space-x-2">
      <span className="text-sm font-semibold text-blue-900">
        Records per page:
      </span>
      <select
        className="p-2 border border-blue-300 rounded bg-white text-blue-700 text-sm"
        value={pageSize}
        onChange={(e) => handleSizeChange(Number(e.target.value))}
      >
        {[100, 200, 500].map((size) => (
          <option key={size} value={size}>
            {size}
          </option>
        ))}
      </select>
    </div>
  );
};

const Pagination = ({
  total,
  pageSize,
  currentPage,
  onPageChange,
  onPageSizeChange,
}) => {
  // Calculate the total number of pages
  const pageCount = Math.ceil(total / pageSize);

  // If there are no items or only one page, don't render pagination
  if (pageCount <= 1) return null;

  // Calculate the range of page numbers to show
  const getPageNumbers = () => {
    const delta = 2; // Number of pages to show on either side of current page
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= pageCount; i++) {
      if (
        i === 1 ||
        i === pageCount ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  // Safely handle page changes with bounds checking
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pageCount) {
      onPageChange(page);
    }
  };

  // For the page size change, we'll update pageSize and reset to page 1
  const handlePageSizeChange = (newSize) => {
    onPageSizeChange(newSize);
    onPageChange(1); // Reset to first page when changing page size
  };

  // Calculate the range of items being displayed
  const startItem = Math.min((currentPage - 1) * pageSize + 1, total);
  const endItem = Math.min(currentPage * pageSize, total);

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 py-3">
      <span className="text-sm font-semibold text-blue-900">
        Showing {startItem} to {endItem} of {total} entries
      </span>

      <PageSizeSelector
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      <div className="flex items-center space-x-1">
        {/* Previous button */}
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-3 py-1 text-sm rounded-md transition-colors duration-200 
                        ${
                          currentPage === 1
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800"
                        }`}
        >
          Previous
        </button>

        {/* Page numbers */}
        {getPageNumbers().map((page, index) => (
          <button
            key={index}
            onClick={() =>
              typeof page === "number" ? handlePageChange(page) : null
            }
            disabled={page === "..."}
            className={`px-3 py-1 text-sm rounded-md transition-colors duration-200
                            ${
                              typeof page !== "number"
                                ? "bg-white text-gray-600 cursor-default"
                                : page === currentPage
                                ? "bg-blue-600 text-white hover:bg-blue-700"
                                : "bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800"
                            }`}
          >
            {page}
          </button>
        ))}

        {/* Next button */}
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === pageCount}
          className={`px-3 py-1 text-sm rounded-md transition-colors duration-200 
                        ${
                          currentPage === pageCount
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800"
                        }`}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Pagination;
